import { Box, Flex, Text } from '@chakra-ui/react';
import React, { useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { route } from 'nextjs-routes';

import useApiQuery, { getResourceKey } from 'lib/api/useApiQuery';
import { AddressHighlightProvider } from 'lib/contexts/addressHighlight';
import useIsMobile from 'lib/hooks/useIsMobile';
import useNewTxsSocket from 'lib/hooks/useNewTxsSocket';
import useSocketChannel from 'lib/socket/useSocketChannel';
import useSocketMessage from 'lib/socket/useSocketMessage';
import useInitialList from 'lib/hooks/useInitialList';
import { TX } from 'stubs/tx';
import { Link } from 'toolkit/chakra/link';
import { Heading } from 'toolkit/chakra/heading';
import type { Transaction } from 'types/api/transaction';

import LatestTxsItem from './LatestTxsItem';
import LatestTxsItemMobile from './LatestTxsItemMobile';

const LatestTransactions = () => {
  const isMobile = useIsMobile();
  const txsCount = 6; // 无论是移动端还是桌面端都显示6条
  const { data, isPlaceholderData, isError } = useApiQuery('homepage_txs', {
    queryOptions: {
      placeholderData: Array(txsCount).fill(TX),
      refetchInterval: 4200,
      refetchIntervalInBackground: false,
    },
  });

  // 初始化列表状态管理（用于动画效果）
  const initialList = useInitialList({
    data: data ?? [],
    idFn: (tx) => tx.hash,
    enabled: !isPlaceholderData,
  });

  // 获取queryClient用于更新数据
  const queryClient = useQueryClient();
  const { num, socketAlert } = useNewTxsSocket();

  // 处理新交易消息
  const handleNewTxMessage = React.useCallback((payload: any) => {
    // 检查页面是否可见
    if (document.visibilityState === 'visible') {
      // console.log('New transaction notification received (visible)', payload);
      queryClient.invalidateQueries({
        queryKey: getResourceKey('homepage_txs'),
      });
    } else {
      // console.log('New transaction notification received (hidden), skipping refetch', payload);
    }
  }, [queryClient]);

  // 建立socket连接
  const channel = useSocketChannel({
    topic: 'transactions:new_transaction',
    isDisabled: isPlaceholderData || isError,
  });

  // 监听新交易消息
  useSocketMessage({
    channel,
    event: 'transaction',
    handler: handleNewTxMessage,
  });

  if (isError) {
    return <Text mt={4}>No data. Please reload the page.</Text>;
  }

  if (data) {
    const txsUrl = route({ pathname: '/txs' });
    return (
      <Box width="100%" flexShrink={0} bgColor="white" _dark={{ bgColor: "gray.800" }} borderRadius={{ base: 0, md: "md" }} boxShadow={{
        _light: "0 0.5rem 1.2rem rgba(189, 197, 209, 0.2)",
        _dark: "0 0.5rem 1.2rem rgba(20, 20, 21, 0.4)"
      }} border="1px solid" borderColor={{ _light: '#e9ecef', _dark: 'whiteAlpha.200' }} overflow="hidden">
        <Flex justifyContent="space-between" alignItems="center" p={{ base: 3, md: 4 }} borderBottomWidth="1px" borderColor="gray.100" _dark={{ borderColor: "gray.700" }}>
          <Heading level="3" fontSize="lg" fontWeight="semibold" color={{ _light: '#212529', _dark: 'gray.400' }}>Latest Transactions</Heading>
        </Flex>

        <Box position="relative">
          <AddressHighlightProvider>
            {/* 移动端显示 */}
            <Box>
              {data.slice(0, txsCount).map((tx, index) => (
                <Box key={tx.hash + (isPlaceholderData ? index : '')} display={{ base: 'block', lg: 'none' }}>
                  <LatestTxsItemMobile
                    tx={tx}
                    isLoading={isPlaceholderData}
                    animation={initialList.getAnimationProp(tx)}
                  />
                </Box>
              ))}
            </Box>

            {/* 桌面端显示 */}
            <Box>
              {data.slice(0, txsCount).map((tx, index) => (
                <Box key={tx.hash + (isPlaceholderData ? index : '')} display={{ base: 'none', lg: 'block' }}>
                  <LatestTxsItem
                    tx={tx}
                    isLoading={isPlaceholderData}
                    animation={initialList.getAnimationProp(tx)}
                  />
                </Box>
              ))}
            </Box>
          </AddressHighlightProvider>
          <Link
            href={txsUrl}
            _hover={{ textDecoration: 'none' }}
            display="block"
            width="100%"
          >
            <Flex
              justifyContent="center"
              py={3}
              borderTopWidth="1px"
              borderColor="gray.100"
              bg="gray.50"
              _dark={{ bg: "gray.700", borderColor: "gray.700" }}
              _hover={{ bg: "gray.100", _dark: { bg: "gray.600" } }}
              transition="background 0.2s"
            >
              <Text
                textStyle="sm"
                color="gray.600"
                _dark={{ color: "gray.200" }}
                fontWeight="medium"
              >
                View all transactions
              </Text>
            </Flex>
          </Link>
        </Box>
      </Box>
    );
  }

  return null;
};

export default LatestTransactions;
